import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import AnonymousForm from './AnonymousForm';

type AuthMode = 'login' | 'register' | 'anonymous';

export function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  // Get initial mode from URL params or default to login
  const initialMode = (searchParams.get('mode') as AuthMode) || 'login';
  const [currentMode, setCurrentMode] = useState<AuthMode>(initialMode);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  // Update URL when mode changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('mode', currentMode);
    navigate(`/auth?${newSearchParams.toString()}`, { replace: true });
  }, [currentMode, navigate, searchParams]);

  const handleAuthSuccess = () => {
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  };

  const renderCurrentForm = () => {
    switch (currentMode) {
      case 'login':
        return (
          <LoginForm
            onSuccess={handleAuthSuccess}
            onSwitchToRegister={() => setCurrentMode('register')}
            onSwitchToAnonymous={() => setCurrentMode('anonymous')}
          />
        );
      case 'register':
        return (
          <RegisterForm
            onSuccess={handleAuthSuccess}
            onSwitchToLogin={() => setCurrentMode('login')}
            onSwitchToAnonymous={() => setCurrentMode('anonymous')}
          />
        );
      case 'anonymous':
        return (
          <AnonymousForm
            onSuccess={handleAuthSuccess}
            onSwitchToLogin={() => setCurrentMode('login')}
            onSwitchToRegister={() => setCurrentMode('register')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>Proximity Messaging</h1>
          <p>Connect with people nearby</p>
        </div>

        <div className="auth-tabs">
          <button
            className={`tab-button ${currentMode === 'login' ? 'active' : ''}`}
            onClick={() => setCurrentMode('login')}
          >
            Sign In
          </button>
          <button
            className={`tab-button ${currentMode === 'register' ? 'active' : ''}`}
            onClick={() => setCurrentMode('register')}
          >
            Sign Up
          </button>
          <button
            className={`tab-button ${currentMode === 'anonymous' ? 'active' : ''}`}
            onClick={() => setCurrentMode('anonymous')}
          >
            Guest
          </button>
        </div>

        <div className="auth-form-container">
          {renderCurrentForm()}
        </div>

        <div className="auth-footer">
          <p>
            By using this app, you agree to our terms of service and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;